import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router';
import { meetingsApi } from '../../api/meetings';
import { Button, ErrorBanner, Loading } from '../../components';
import { formatDate, formatDateTime, formatDuration, formatOffset } from '../../lib/format';
import { OUTCOME_LABELS, PLATFORM_LABELS, SENTIMENT_LABELS } from '../../lib/labels';

/**
 * Print-friendly meeting (summary + transcript). Opens the browser's print
 * dialog once loaded, so the user can "Save as PDF". The export itself is
 * audit-logged by the caller (POST /meetings/:id/audit) before navigating here.
 */
export function MeetingPrintPage() {
  const { id = '' } = useParams();
  const meeting = useQuery({ queryKey: ['meeting', id], queryFn: () => meetingsApi.get(id) });
  const transcript = useQuery({
    queryKey: ['transcript', id],
    queryFn: () => meetingsApi.transcript(id),
    enabled: meeting.data?.transcriptAvailable === true,
    staleTime: Infinity,
  });
  const printed = useRef(false);
  const ready = meeting.isSuccess && (!meeting.data.transcriptAvailable || transcript.isSuccess || transcript.isError);

  useEffect(() => {
    if (!ready || printed.current) return;
    printed.current = true;
    const t = setTimeout(() => window.print(), 300);
    return () => clearTimeout(t);
  }, [ready]);

  if (meeting.isLoading) return <Loading />;
  if (meeting.isError) return <ErrorBanner error={meeting.error} className="m-6" />;
  const m = meeting.data;
  if (!m) return null;
  const s = m.summary;

  return (
    <div className="mx-auto max-w-3xl bg-white p-8 text-[13px] text-black print:max-w-none print:p-0">
      <div className="mb-6 flex gap-2 print:hidden">
        <Link to={`/meetings/${m.id}`} className="text-sm text-blue-700 hover:underline">
          ← Back to meeting
        </Link>
        <div className="flex-1" />
        <Button size="sm" variant="primary" onClick={() => window.print()}>
          Print / Save as PDF
        </Button>
      </div>

      <h1 className="text-xl font-semibold">{m.title || 'Untitled meeting'}</h1>
      <p className="mt-1 text-gray-600">
        {formatDateTime(m.startedAt)} · {formatDuration(m.durationSec)} · {PLATFORM_LABELS[m.platform]} · Rep: {m.repName}
        {m.clientName && ` · Client: ${m.clientName}`}
        {m.outcome && ` · Outcome: ${OUTCOME_LABELS[m.outcome]}`}
      </p>
      {m.contacts.length > 0 && (
        <p className="mt-1 text-gray-600">Attendees: {m.contacts.map((c) => c.name || c.email).join(', ')}</p>
      )}

      {s ? (
        <>
          <PrintSection title="Summary" items={s.summary} />
          {s.sentiment && (
            <p className="mt-2">
              <strong>Client sentiment:</strong> {SENTIMENT_LABELS[s.sentiment.level]}
              {s.sentiment.reason && `: ${s.sentiment.reason}`}
            </p>
          )}
          {s.nextSteps.length > 0 && (
            <section className="mt-5 break-inside-avoid">
              <h2 className="mb-1 text-sm font-semibold uppercase tracking-wide text-gray-700">Next steps</h2>
              <ul className="list-disc pl-5">
                {s.nextSteps.map((n, i) => (
                  <li key={i}>
                    {n.text}
                    {(n.owner || n.dueDate) && (
                      <span className="text-gray-600">
                        {' '}
                        ({[n.owner, n.dueDate && `due ${formatDate(n.dueDate)}`].filter(Boolean).join(', ')})
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}
          <PrintSection title="Pain points" items={s.painPoints} />
          <PrintSection title="Requirements" items={s.requirements} />
          <PrintSection title="Objections" items={s.objections} />
          <section className="mt-5 break-inside-avoid">
            <h2 className="mb-1 text-sm font-semibold uppercase tracking-wide text-gray-700">Follow-up email</h2>
            <p className="font-medium">Subject: {s.followUpEmail.subject}</p>
            <pre className="mt-1 font-sans whitespace-pre-wrap">{s.followUpEmail.body}</pre>
          </section>
        </>
      ) : (
        <p className="mt-5 text-gray-600">No summary available.</p>
      )}

      <section className="mt-6">
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-700">Transcript</h2>
        {!m.transcriptAvailable ? (
          <p className="text-gray-600">No transcript stored.</p>
        ) : transcript.isLoading ? (
          <Loading label="Loading transcript…" />
        ) : transcript.isError ? (
          <ErrorBanner error={transcript.error} />
        ) : (
          <table className="w-full">
            <tbody>
              {(transcript.data ?? []).map((seg) => (
                <tr key={seg.id} className="break-inside-avoid align-top">
                  <td className="w-14 py-0.5 pr-2 font-mono text-[11px] text-gray-500">{formatOffset(seg.startMs)}</td>
                  <td className="w-12 py-0.5 pr-2 font-semibold">{seg.source === 'me' ? 'Me' : 'Them'}</td>
                  <td className="py-0.5">{seg.text}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function PrintSection({ title, items }: { title: string; items: string[] }) {
  if (items.length === 0) return null;
  return (
    <section className="mt-5 break-inside-avoid">
      <h2 className="mb-1 text-sm font-semibold uppercase tracking-wide text-gray-700">{title}</h2>
      <ul className="list-disc pl-5">
        {items.map((t, i) => (
          <li key={i}>{t}</li>
        ))}
      </ul>
    </section>
  );
}
